import { useEffect, useRef } from "react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { X, Download, Share2, FileText, MessageCircle } from "lucide-react";
import Btn from "./Btn";

const RANK_LABEL = { 1: "JUARA 1", 2: "JUARA 2", 3: "JUARA 3" };
const RANK_COLOR = { 1: "#FFC800", 2: "#CBD5E1", 3: "#D97706" };

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export default function CertificateModal({ open, onClose, name, rank, eventTitle, exam, score, dateIso }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!open || !canvasRef.current) return;
    const c = canvasRef.current;
    const W = 1400, H = 990;
    c.width = W; c.height = H;
    const ctx = c.getContext("2d");
    const accent = RANK_COLOR[rank] || "#FFC800";

    // background
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, "#0A1020");
    g.addColorStop(1, "#0F1B2E");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);

    // decorative corner glows
    ctx.save();
    ctx.globalAlpha = 0.12;
    ctx.fillStyle = accent;
    ctx.beginPath(); ctx.arc(80, 80, 260, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#00E5FF";
    ctx.beginPath(); ctx.arc(W - 60, H - 40, 300, 0, Math.PI * 2); ctx.fill();
    ctx.restore();

    // outer border
    ctx.lineWidth = 6; ctx.strokeStyle = accent;
    roundRect(ctx, 40, 40, W - 80, H - 80, 28); ctx.stroke();
    ctx.lineWidth = 2; ctx.strokeStyle = "rgba(255,255,255,0.15)";
    roundRect(ctx, 60, 60, W - 120, H - 120, 20); ctx.stroke();

    const cx = W / 2;
    ctx.textAlign = "center";

    ctx.fillStyle = "#00E5FF";
    ctx.font = "800 30px Arial, sans-serif";
    ctx.fillText("SKORUP.IO", cx, 150);

    ctx.fillStyle = "#ffffff";
    ctx.font = "900 74px Georgia, 'Times New Roman', serif";
    ctx.fillText("SERTIFIKAT PENGHARGAAN", cx, 232);

    ctx.fillStyle = accent;
    ctx.font = "800 34px Arial, sans-serif";
    ctx.fillText("🏆  TRYOUT AKBAR  🏆", cx, 292);

    // medal badge
    ctx.beginPath(); ctx.arc(cx, 400, 66, 0, Math.PI * 2);
    ctx.fillStyle = accent; ctx.fill();
    ctx.fillStyle = "#0A1020";
    ctx.font = "900 60px Arial, sans-serif";
    ctx.fillText(String(rank), cx, 422);
    ctx.fillStyle = accent;
    ctx.font = "900 30px Arial, sans-serif";
    ctx.fillText(RANK_LABEL[rank] || `JUARA ${rank}`, cx, 508);

    ctx.fillStyle = "#94a3b8";
    ctx.font = "500 26px Arial, sans-serif";
    ctx.fillText("Dengan bangga diberikan kepada", cx, 566);

    ctx.fillStyle = "#ffffff";
    ctx.font = "900 68px Georgia, serif";
    ctx.fillText(name || "Peserta", cx, 638);
    // underline
    const nameW = Math.min(ctx.measureText(name || "Peserta").width + 80, W - 240);
    ctx.strokeStyle = "rgba(255,255,255,0.25)"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(cx - nameW / 2, 662); ctx.lineTo(cx + nameW / 2, 662); ctx.stroke();

    ctx.fillStyle = "#cbd5e1";
    ctx.font = "500 27px Arial, sans-serif";
    ctx.fillText(`atas pencapaian gemilang meraih Peringkat #${rank}`, cx, 716);
    ctx.fillText(`dengan skor ${score ?? "-"} pada "${eventTitle || "Tryout Akbar"}" (${exam || "CPNS"})`, cx, 752);

    const dt = dateIso ? new Date(dateIso) : new Date();
    const dateStr = dt.toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
    ctx.fillStyle = "#64748b";
    ctx.font = "600 24px Arial, sans-serif";
    ctx.fillText(dateStr, cx, 830);

    ctx.fillStyle = accent;
    ctx.font = "800 26px Arial, sans-serif";
    ctx.fillText("SkorUp.io — Platform Persiapan CPNS & SNBT", cx, 895);
  }, [open, name, rank, eventTitle, exam, score, dateIso]);

  if (!open) return null;

  const fileName = `Sertifikat-Juara-SkorUp-${(name || "peserta").replace(/\s+/g, "-")}.png`;
  const caption = `Alhamdulillah, aku meraih Peringkat #${rank} di Tryout Akbar "${eventTitle}" SkorUp.io! 🏆🔥 Yuk latihan CPNS & SNBT bareng di SkorUp.io!`;
  const appUrl = window.location.origin;

  const downloadPng = () => {
    const url = canvasRef.current.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url; a.download = fileName; a.click();
    toast.success("Sertifikat (PNG) diunduh!");
  };

  const downloadPdf = () => {
    const img = canvasRef.current.toDataURL("image/png");
    const doc = new jsPDF({ unit: "pt", format: [1400, 990], orientation: "landscape" });
    doc.addImage(img, "PNG", 0, 0, 1400, 990);
    doc.save(fileName.replace(".png", ".pdf"));
    toast.success("Sertifikat (PDF) diunduh!");
  };

  const shareCert = async () => {
    canvasRef.current.toBlob(async (blob) => {
      const file = new File([blob], fileName, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ title: "Sertifikat Juara Tryout Akbar", text: caption, files: [file] });
          return;
        } catch { /* user cancelled or unsupported */ }
      }
      downloadPng();
      toast.info("Sertifikat diunduh — lampirkan ke postingan media sosialmu ya! 📸");
    }, "image/png");
  };

  const shareWA = () => window.open(`https://wa.me/?text=${encodeURIComponent(caption + " " + appUrl)}`, "_blank");
  const shareTW = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(appUrl)}`, "_blank");
  const shareFB = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(appUrl)}&quote=${encodeURIComponent(caption)}`, "_blank");

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" onClick={onClose} data-testid="certificate-modal">
      <div className="card-chunky p-5 max-w-3xl w-full my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-black text-lg">Sertifikat Juara 🎖️</h3>
          <button onClick={onClose} data-testid="certificate-close" className="w-9 h-9 grid place-items-center rounded-xl border-2 border-su-border"><X className="w-4 h-4" /></button>
        </div>

        <div className="rounded-2xl overflow-hidden border-2 border-su-border">
          <canvas ref={canvasRef} className="w-full h-auto block" data-testid="certificate-canvas" />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4">
          <Btn data-testid="cert-download-png" onClick={downloadPng}><Download className="w-4 h-4" /> PNG</Btn>
          <Btn variant="ghost" data-testid="cert-download-pdf" onClick={downloadPdf}><FileText className="w-4 h-4" /> PDF</Btn>
          <Btn variant="secondary" data-testid="cert-share" onClick={shareCert}><Share2 className="w-4 h-4" /> Bagikan</Btn>
        </div>
        <div className="flex items-center justify-center gap-2 mt-3">
          <span className="text-xs font-heading font-bold text-su-muted mr-1">Bagikan ke:</span>
          <button data-testid="cert-share-wa" onClick={shareWA} className="w-9 h-9 grid place-items-center rounded-full border-2 border-su-border hover:border-su-success transition-colors"><MessageCircle className="w-4 h-4 text-su-success" /></button>
          <button data-testid="cert-share-tw" onClick={shareTW} className="w-9 h-9 grid place-items-center rounded-full border-2 border-su-border hover:border-su-primary transition-colors"><Share2 className="w-4 h-4 text-su-primary" /></button>
          <button data-testid="cert-share-fb" onClick={shareFB} className="w-9 h-9 grid place-items-center rounded-full border-2 border-su-border hover:border-su-primary transition-colors"><span className="font-heading font-black text-su-primary text-sm">f</span></button>
        </div>
      </div>
    </div>
  );
}
